// What a visitor's browser says about the screen and the reader, for EsAnalytics (GA4)
// and EsRum (New Relic), so Tim can check the test widths against real players. One
// snapshot, two spellings: camelCase for New Relic attributes, snake_case for GA params.
//
// Coarse on purpose (Tim, 2026-10-24: "what's available and not creepy"). Family and
// major version only, never the user-agent string; no canvas, WebGL, fonts, plugins,
// battery, memory, core count, or timezone; no identifier of any kind. Each field is a
// value thousands of visitors share. A change here is a change to the privacy page.
//
// Names stay clear of what each tool already fills in: New Relic's PageView has
// deviceType, userAgentName, userAgentOS, and userAgentVersion, so this says deviceKind,
// browserFamily, osFamily, and browserMajor.
//
// Imports nothing, so both callers can hold it in every build at no cost.

// Tailwind 4's default breakpoints: a band starts at its width, and below sm is xs.
export const ES_BANDS = [ [ '2xl', 1536 ], [ 'xl', 1280 ], [ 'lg', 1024 ], [ 'md', 768 ], [ 'sm', 640 ] ];

export const esBand = ( pWidth ) => {
    const aBand = ES_BANDS.find( ( [ , pMin ] ) => pWidth >= pMin );
    return aBand ? aBand[ 0 ] : 'xs';
};

// Null when the browser has no matchMedia (jsdom, very old engines), rather than a guess.
const media = ( pQuery ) => {
    try {
        return typeof window.matchMedia === 'function' ? window.matchMedia( pQuery ).matches : null;
    } catch {
        return null;
    }
};

const firstMatch = ( pFeature, pValues ) => {
    if ( typeof window.matchMedia !== 'function' ) return null;
    return pValues.find( ( pValue ) => media( '(' + pFeature + ': ' + pValue + ')' ) ) || null;
};

// userAgentData lists a made-up "Not A Brand" entry (GREASE) and Chromium beside the
// real browser; the real one is whatever is left.
const UAD_BRANDS = { 'Google Chrome': 'Chrome', 'Microsoft Edge': 'Edge', 'Opera': 'Opera', 'Brave': 'Brave' };
const UAD_OS = { 'macOS': 'macOS', 'Windows': 'Windows', 'Android': 'Android', 'Chrome OS': 'ChromeOS',
    'Chromium OS': 'ChromeOS', 'Linux': 'Linux', 'iOS': 'iOS' };

const fromUserAgentData = ( pData ) => {
    const aBrands = ( pData.brands || [] ).filter( ( pBrand ) => !/not.?a.?brand/i.test( pBrand.brand ) );
    const aBrand = aBrands.find( ( pBrand ) => UAD_BRANDS[ pBrand.brand ] )
        || aBrands.find( ( pBrand ) => pBrand.brand !== 'Chromium' )
        || aBrands[ 0 ];
    const aOs = UAD_OS[ pData.platform ] || 'other';
    return {
        browserFamily: aBrand ? ( UAD_BRANDS[ aBrand.brand ] || aBrand.brand ) : 'other',
        browserMajor: aBrand ? parseInt( aBrand.version, 10 ) || null : null,
        osFamily: aOs,
        deviceKind: pData.mobile ? 'phone' : ( aOs === 'Android' ? 'tablet' : 'desktop' ),
    };
};

// Order matters: Edge and Opera also say Chrome, and Chrome also says Safari.
const UA_BROWSERS = [
    [ /Edg(?:e|A|iOS)?\/(\d+)/, 'Edge' ],
    [ /(?:OPR|OPX)\/(\d+)/, 'Opera' ],
    [ /SamsungBrowser\/(\d+)/, 'Samsung Internet' ],
    [ /(?:Firefox|FxiOS)\/(\d+)/, 'Firefox' ],
    [ /(?:Chrome|CriOS)\/(\d+)/, 'Chrome' ],
    [ /Version\/(\d+)[^ ]* (?:Mobile\/\S+ )?Safari\//, 'Safari' ],
];

const fromUserAgent = ( pUa, pTouchPoints ) => {
    const aHit = UA_BROWSERS.map( ( [ pPattern, pName ] ) => [ pUa.match( pPattern ), pName ] )
        .find( ( [ pMatch ] ) => pMatch );
    // iPadOS 13 and later reports itself as a Mac; only the touch screen gives it away.
    const aIpad = /iPad/.test( pUa ) || ( /Macintosh/.test( pUa ) && pTouchPoints > 1 );
    let aOs = 'other';
    if ( /iPhone|iPod/.test( pUa ) || aIpad ) aOs = 'iOS';
    else if ( /Android/.test( pUa ) ) aOs = 'Android';
    else if ( /CrOS/.test( pUa ) ) aOs = 'ChromeOS';
    else if ( /Windows/.test( pUa ) ) aOs = 'Windows';
    else if ( /Macintosh|Mac OS X/.test( pUa ) ) aOs = 'macOS';
    else if ( /Linux/.test( pUa ) ) aOs = 'Linux';

    let aKind = 'desktop';
    if ( /iPhone|iPod/.test( pUa ) || ( aOs === 'Android' && /Mobile/.test( pUa ) ) ) aKind = 'phone';
    else if ( aIpad || aOs === 'Android' ) aKind = 'tablet';

    return {
        browserFamily: aHit ? aHit[ 1 ] : 'other',
        browserMajor: aHit ? parseInt( aHit[ 0 ][ 1 ], 10 ) : null,
        osFamily: aOs,
        deviceKind: aKind,
    };
};

const orientation = () => {
    const aType = window.screen && window.screen.orientation && window.screen.orientation.type;
    if ( typeof aType === 'string' ) return aType.startsWith( 'portrait' ) ? 'portrait' : 'landscape';
    return window.innerHeight > window.innerWidth ? 'portrait' : 'landscape';
};

const displayMode = () => {
    if ( navigator.standalone === true ) return 'standalone';
    return firstMatch( 'display-mode', [ 'fullscreen', 'standalone', 'minimal-ui', 'browser' ] );
};

const touch = () => ( typeof navigator.maxTouchPoints === 'number' ? navigator.maxTouchPoints > 0 : null );

/**
 * The visitor's browser as one flat object of strings, numbers, booleans, and nulls,
 * camelCase. Null means the browser does not say.
 */
export const esDevice = () => {
    const aUad = navigator.userAgentData;
    const aClient = aUad && Array.isArray( aUad.brands ) && aUad.brands.length
        ? fromUserAgentData( aUad )
        : fromUserAgent( navigator.userAgent || '', navigator.maxTouchPoints || 0 );
    const aConnection = navigator.connection && navigator.connection.effectiveType;
    const aScreen = window.screen || {};

    return {
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        screenWidth: aScreen.width ?? null,
        screenHeight: aScreen.height ?? null,
        // Two places is all a layout question needs; more would only split the counts.
        devicePixelRatio: window.devicePixelRatio ? Math.round( window.devicePixelRatio * 100 ) / 100 : null,
        orientation: orientation(),
        band: esBand( window.innerWidth ),
        ...aClient,
        pointer: firstMatch( 'pointer', [ 'coarse', 'fine', 'none' ] ),
        hover: media( '(hover: hover)' ),
        touch: touch(),
        prefersColorScheme: firstMatch( 'prefers-color-scheme', [ 'dark', 'light' ] ),
        prefersReducedMotion: media( '(prefers-reduced-motion: reduce)' ),
        prefersContrast: firstMatch( 'prefers-contrast', [ 'more', 'less', 'custom', 'no-preference' ] ),
        // EsThemeContext's resolved mode, which it puts on <html> as `dark`. The mode is
        // set per environment, not per reader, so this mostly says what `system` became.
        appScheme: document.documentElement.classList.contains( 'dark' ) ? 'dark' : 'light',
        browserLanguage: navigator.language || null,
        displayMode: displayMode(),
        connectionType: typeof aConnection === 'string' ? aConnection : null,
    };
};

/** The same snapshot with GA4's snake_case parameter names. */
export const esDeviceParams = ( pDevice ) => Object.fromEntries(
    Object.entries( pDevice ).map( ( [ pKey, pValue ] ) =>
        [ pKey.replace( /[A-Z]/g, ( pUpper ) => '_' + pUpper.toLowerCase() ), pValue ] ) );

// Long enough that dragging a window edge is one update, not sixty.
export const ES_DEVICE_SETTLE_MS = 500;

const sWatchers = new Set();
let sInstalled = false;
let sTimer = null;

const notify = () => {
    clearTimeout( sTimer );
    sTimer = setTimeout( () => {
        const aDevice = esDevice();
        sWatchers.forEach( ( pWatcher ) => pWatcher( aDevice ) );
    }, ES_DEVICE_SETTLE_MS );
};

// Installed once for the page however often React mounts, StrictMode's double mount
// included; the listeners live as long as the page, so there is nothing to remove.
const install = () => {
    if ( sInstalled ) return;
    sInstalled = true;
    window.addEventListener( 'resize', notify );
    window.addEventListener( 'orientationchange', notify );
    if ( typeof window.matchMedia === 'function' ) {
        [ '(prefers-color-scheme: dark)', '(prefers-reduced-motion: reduce)', '(prefers-contrast: more)',
            '(display-mode: standalone)', '(display-mode: fullscreen)' ].forEach( ( pQuery ) => {
            try {
                const aList = window.matchMedia( pQuery );
                if ( aList && typeof aList.addEventListener === 'function' ) aList.addEventListener( 'change', notify );
            } catch {
                // An engine that cannot parse one query still reports the others.
            }
        } );
    }
    if ( typeof MutationObserver === 'function' ) {
        new MutationObserver( notify ).observe( document.documentElement, { attributes: true, attributeFilter: [ 'class' ] } );
    }
};

/** Calls pWatcher with a fresh snapshot after the window settles from a change. */
export const esDeviceWatch = ( pWatcher ) => {
    sWatchers.add( pWatcher );
    install();
};
